import type { UgcFlowkitBatch, UgcFlowkitBatchSummary, UgcFlowkitProductStatus } from './ugcFlowkit';

/** One product of today's production list ("Sản xuất hôm nay"), from whichever batch matters for it. */
export interface UgcTodayRow {
  id: string;
  name: string;
  status: UgcFlowkitProductStatus;
  safeStopCode?: string;
  batchId: string;
  copies: number;
}

type SummaryLike = Pick<UgcFlowkitBatchSummary, 'id' | 'createdAt' | 'updatedAt' | 'productsSummary'>;
type LiveBatch = Pick<UgcFlowkitBatch, 'id' | 'createdAt' | 'products'>;

const VN_OFFSET_MS = 7 * 3600000;
export const vnDay = (t: string | number) => new Date((typeof t === 'number' ? t : Date.parse(t)) + VN_OFFSET_MS).toISOString().slice(0, 10);

// Which attempt of a product counts when it appears in several batches today.
const RANK: Record<string, number> = { merged: 4, generating: 3, provisioned: 3, validated: 3, planning: 3, queued: 2, stopped_safe: 1 };

/**
 * Owner 01/11: the list and the batch panel below it must agree, and rows must not jump around while batches run.
 * - The batch the panel shows (polled every 5 s) replaces its own, older summary (polled every 30 s).
 * - Rows keep a fixed order: the start time of the first batch that had the product today, then its position there.
 */
export function buildTodayRows(batches: SummaryLike[], opts: { now?: number; liveBatch?: LiveBatch | null } = {}): UgcTodayRow[] {
  const now = opts.now ?? Date.now();
  const today = vnDay(now);
  const live = opts.liveBatch;
  const merged: SummaryLike[] = batches.map(b => live && b.id === live.id
    ? { ...b, updatedAt: new Date(now).toISOString(), productsSummary: liveProducts(live) }
    : b);
  if (live && !merged.some(b => b.id === live.id)) {
    merged.push({ id: live.id, createdAt: live.createdAt, updatedAt: new Date(now).toISOString(), productsSummary: liveProducts(live) });
  }
  merged.sort((x, y) => Date.parse(x.createdAt) - Date.parse(y.createdAt) || x.id.localeCompare(y.id));

  const best = new Map<string, UgcTodayRow>();
  const order = new Map<string, number>();
  let seq = 0;
  for (const b of merged) {
    const createdToday = vnDay(b.createdAt) === today;
    if (!createdToday && vnDay(b.updatedAt || b.createdAt) !== today) continue;
    for (const p of b.productsSummary) {
      if (p.safeStopCode === 'UGC_DUPLICATE_ELSEWHERE') continue;
      if (!createdToday && p.status === 'stopped_safe') continue; // an older batch counts only for what it finished or runs today
      if (!order.has(p.id)) order.set(p.id, seq++);
      const cur = best.get(p.id);
      // batches are walked oldest first, so on a tie the newer attempt wins
      if (!cur || (RANK[p.status] ?? 0) >= (RANK[cur.status] ?? 0)) {
        best.set(p.id, { id: p.id, name: p.name, status: p.status, safeStopCode: p.safeStopCode, batchId: b.id, copies: (cur?.copies ?? 0) + 1 });
      } else cur.copies += 1;
    }
  }
  return [...best.values()].sort((x, y) => order.get(x.id)! - order.get(y.id)!);
}

function liveProducts(live: LiveBatch): SummaryLike['productsSummary'] {
  return live.products.map(p => ({ id: p.id, name: p.name, status: p.status, safeStopCode: p.safeStop?.code }));
}

export function todayTotals(rows: UgcTodayRow[]) {
  const is = (...s: UgcFlowkitProductStatus[]) => rows.filter(r => s.includes(r.status)).length;
  return {
    products: rows.length,
    merged: is('merged'),
    active: is('planning', 'validated', 'provisioned', 'generating'),
    queued: is('queued'),
    stopped: is('stopped_safe'),
  };
}
